import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, Keyboard, TouchableWithoutFeedback } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { ChatWindow } from '@/components/admin/messages/ChatWindow';
import { ChatOptionsMenu } from '@/components/admin/messages/ChatOptionsMenu';
import { ThreadOwnerModal } from '@/components/admin/messages/ThreadOwnerModal';
import { ChatDetailsModal } from '@/components/admin/messages/ChatDetailsModal';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useConversation } from '@/hooks/admin/useMessages';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';

type ChatScreenProps = StackScreenProps<AdminStackParamList, 'Chat'>;

export const ChatScreen: React.FC<ChatScreenProps> = ({
  navigation,
  route,
}) => {
  const { redesign } = useAdminColors();
  const { conversationId } = route.params;
  const { conversation } = useConversation(conversationId);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showThreadOwnerModal, setShowThreadOwnerModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Render chat header
  const renderChatHeader = () => {
    if (!conversation) return null;

    const conversationAccounts = (conversation.accounts || []).filter(
      (acct) => acct.ConversationAccount?.type === 'resident'
    );

    const recipientName = conversationAccounts.length === 1
      ? conversationAccounts[0].firstName && conversationAccounts[0].lastName
        ? `${conversationAccounts[0].firstName} ${conversationAccounts[0].lastName}`
        : conversationAccounts[0].email
      : `${conversationAccounts.length} Recipients`;

    return (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        <View
          style={[
            styles.chatHeader,
            {
              backgroundColor: redesign.bg.base,
              borderBottomColor: redesign.card.border,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>

          <View style={styles.chatHeaderContent}>
            <Text
              style={[styles.chatHeaderTitle, { color: redesign.text.primary }]}
              numberOfLines={1}
            >
              {conversation.subject}
            </Text>
            <Text
              style={[styles.chatHeaderSubtitle, { color: redesign.text.muted }]}
              numberOfLines={1}
            >
              {recipientName}
            </Text>
          </View>

          <TouchableOpacity
            style={styles.chatMenuButton}
            onPress={() => setShowOptionsMenu(true)}
          >
            <SVG_COMPONENTS.EDIT_DOT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>
        </View>
      </TouchableWithoutFeedback>
    );
  };

  return (
    <AdminErrorBoundary context="ChatScreen">
      <SafeAreaView style={[styles.fullContainer, { backgroundColor: redesign.bg.base }]}>
        <TouchableWithoutFeedback onPress={() => setShowOptionsMenu(false)}>
          <KeyboardAvoidingView
            style={styles.keyboardContainer}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
            enabled={true}
          >
            {renderChatHeader()}
            <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
              <ChatWindow conversationId={conversationId} />
            </View>

            {/* Chat Options Menu */}
            {conversation && (
              <ChatOptionsMenu
                visible={showOptionsMenu}
                onClose={() => setShowOptionsMenu(false)}
                conversation={conversation}
                onEditPress={() => navigation.navigate('EditMessage', { conversationId })}
                onThreadOwnerPress={() => setShowThreadOwnerModal(true)}
                onShowDetailsPress={() => setShowDetailsModal(true)}
                position={{ top: 64, right: 16 }}
              />
            )}

            {/* Thread Owner Modal */}
            {conversation && (
              <ThreadOwnerModal
                visible={showThreadOwnerModal}
                onClose={() => setShowThreadOwnerModal(false)}
                conversation={conversation}
              />
            )}

            {/* Chat Details Modal */}
            {conversation && (
              <ChatDetailsModal
                visible={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                conversation={conversation}
                onParticipantPress={(participant) => {
                  const accountId = participant?.assigneeAccount?.id;
                  const leaseId = participant?.assigneeAccount?.leaseCustomers?.[0]?.lease?.id;
                  if (accountId && leaseId) {
                    setShowDetailsModal(false);
                    navigation.navigate('ResidentDetails', { accountId, leaseId, mode: 'account' });
                  }
                }}
              />
            )}
          </KeyboardAvoidingView>
        </TouchableWithoutFeedback>
      </SafeAreaView>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  fullContainer: {
    flex: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  chatHeaderContent: {
    flex: 1,
    marginRight: 8,
  },
  chatHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 2,
  },
  chatHeaderSubtitle: {
    fontSize: 14,
  },
  chatMenuButton: {
    padding: 8,
  },
});
